import { useRef, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Form,
  Row,
  Spinner,
  Table,
} from "react-bootstrap";
import {
  FiCheckCircle,
  FiFileText,
  FiMail,
  FiUploadCloud,
} from "react-icons/fi";
import {
  analyzeProductionSalesSummary,
  sendProductionSalesSummary,
} from "../../../api/planlama/productionSalesSummaryApi";
import Layout from "../../layout/Layout";

const formatTon = (value) =>
  Number(value || 0).toLocaleString("tr-TR", {
    maximumFractionDigits: 0,
  });

const getErrorMessage = (error, fallback) =>
  error?.response?.data?.message || error?.message || fallback;

function ProductionSalesSummaryPage() {
  const fileInputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [report, setReport] = useState(null);
  const [recipientEmail, setRecipientEmail] = useState("");
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  //   Tüm değerler seçilen ayın satırından alınıyor, yıllık değerler ise o bölümün TOPLAM satırından. Kg 1000'e bölünüp küsurat atılarak tona çevriliyor.

  // Tablodaki alan	Hesap
  // Ordino satış – Ay	G
  // Ordino satış – Yıl	TOPLAM satırının G’si
  // Ordino satış – İç	B + D
  // Ordino satış – Dış	C + E
  // Nihai Sevkiyat – Ay	M
  // Nihai Sevkiyat – Yıl	TOPLAM satırının M’si
  // Nihai Sevkiyat – İç	M − (C + E)
  // Nihai Sevkiyat – Dış	C + E
  // Nihai üretim – Ay	P
  // Nihai üretim – Yıl	TOPLAM satırının P’si
  // BP/AY	D + E + F + G + H
  // ÇSM/AY	P − BP
  // Hadde (not)	J
  // Tavlama (not)	K
  // Fason BP (not)	H
  // Aykut Metal / MEKO (not)	H hücresinin yorumu

  ///

  // ORDİNO – Ekim satırı
  // B=1.200.000  C=2.500.000  D=300.000  E=500.000  G=4.500.000
  // Ay  = G           → 4.500.000 / 1000 = 4.500 ton
  // İç  = B + D       → 1.500.000        = 1.500 ton
  // Dış = C + E       → 3.000.000        = 3.000 ton
  // Yıl = TOPLAM satırının G'si (ör. 41.250.800) = 41.250 ton

  // SEVKİYAT – Ekim satırı
  // C=2.000.000  E=600.000  M=4.200.000
  // Ay  = M           → 4.200 ton
  // Dış = C + E       → 2.600 ton
  // İç  = M − (C + E) → 1.600 ton

  // ÜRETİM – Ekim satırı
  // D=800.000 E=700.000 F=600.000 G=500.000 H=400.000  P=5.000.000
  // Ay  = P                  → 5.000 ton
  // BP  = D+E+F+G+H          → 3.000 ton
  // ÇSM = P − BP             → 2.000 ton
  // Fason = H                →   400 ton
  // H yorumu: "MEKO: 250.000 kg  AYKUT METAL: 150.000 kg"
  //   → MEKO 250 ton, Aykut Metal 150 ton (toplam H ile tutuyor, uyarı yok)

  // Ton çevirisinde küsurat atılır: 4.499.999 kg → 4.499 ton (4.500 değil)

  const handleFileChange = (event) => {
    const nextFile = event.target.files?.[0] || null;
    setFile(nextFile);
    setReport(null);
    setRecipientEmail("");
    setError("");
    setSuccess("");
  };

  const handleAnalyze = async () => {
    if (!file) {
      setError("Lütfen önce .xls veya .xlsx uzantılı rapor dosyasını seçin.");
      return;
    }

    setIsAnalyzing(true);
    setError("");
    setSuccess("");

    try {
      const response = await analyzeProductionSalesSummary(file);
      setReport(response.data.data);
      setRecipientEmail("");
    } catch (requestError) {
      setReport(null);
      setError(
        getErrorMessage(requestError, "Excel raporu okunurken bir hata oluştu.")
      );
    } finally {
      setIsAnalyzing(false);
    }
  };

  const handleSend = async () => {
    if (!file || !report) return;
    if (!recipientEmail) {
      setError("Lütfen raporun gönderileceği alıcıyı seçin.");
      return;
    }

    setIsSending(true);
    setError("");
    setSuccess("");

    try {
      const response = await sendProductionSalesSummary(file, recipientEmail);
      setSuccess(
        response.data.message || "Rapor e-posta adresinize gönderildi."
      );
    } catch (requestError) {
      setError(
        getErrorMessage(
          requestError,
          "Rapor e-posta ile gönderilirken bir hata oluştu."
        )
      );
    } finally {
      setIsSending(false);
    }
  };

  const summary = report?.summary;
  const metrics = summary?.metrics;
  const currentYear = new Date().getFullYear();

  return (
    <Layout>
      <div style={{ maxWidth: 1180, margin: "0 auto" }}>
        <div className="mb-4">
          <div className="d-flex align-items-center gap-2 mb-1">
            <FiFileText size={26} className="text-primary" />
            <h3 className="fw-bold m-0">
              {currentYear} YILI AY BAZINDA ÜRETİM-SATIŞ RAKAMLARI
            </h3>
          </div>
          <p className="text-muted mb-0">
            Aylık Excel dosyasını yükleyin; tablo ve e-posta metni otomatik
            hazırlansın.
          </p>
        </div>

        {error && <Alert variant="danger">{error}</Alert>}
        {success && (
          <Alert variant="success" className="d-flex align-items-center gap-2">
            <FiCheckCircle size={20} />
            <span>{success}</span>
          </Alert>
        )}

        <Card className="border-0 shadow-sm mb-4">
          <Card.Body className="p-4">
            <Row className="align-items-end g-3">
              <Col lg={8}>
                <Form.Label className="fw-semibold">
                  Üretim-satış Excel dosyası
                </Form.Label>
                <Form.Control
                  ref={fileInputRef}
                  type="file"
                  accept=".xls,.xlsx,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                  onChange={handleFileChange}
                />
                <Form.Text className="text-muted">
                  Sistem dosyadaki son dolu ayı otomatik algılar. Desteklenen
                  türler: XLS, XLSX.
                </Form.Text>
              </Col>
              <Col lg={4}>
                <Button
                  variant="primary"
                  className="w-100 d-flex align-items-center justify-content-center gap-2"
                  onClick={handleAnalyze}
                  disabled={!file || isAnalyzing || isSending}
                >
                  {isAnalyzing ? (
                    <Spinner animation="border" size="sm" />
                  ) : (
                    <FiUploadCloud size={19} />
                  )}
                  {isAnalyzing ? "Excel okunuyor..." : "Raporu Hazırla"}
                </Button>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        {summary && metrics && (
          <>
            <Card className="border-0 shadow-sm mb-4">
              <Card.Header className="bg-white border-0 px-4 pt-4 pb-2 d-flex flex-wrap justify-content-between align-items-center gap-2">
                <div>
                  <h5 className="fw-bold mb-1">
                    {summary.year} {summary.month.label} özeti
                  </h5>
                  <small className="text-muted">
                    Kaynak sayfa: {summary.selectedSheetName}
                  </small>
                </div>
                <Badge bg="primary" className="px-3 py-2">
                  {summary.month.label.toLocaleUpperCase("tr-TR")}
                </Badge>
              </Card.Header>
              <Card.Body className="p-4">
                <div className="table-responsive">
                  <Table bordered hover className="align-middle mb-0">
                    <thead className="table-dark text-center">
                      <tr>
                        <th aria-label="Kalem"></th>
                        <th>Ay/Ton</th>
                        <th>Yıl/Ton</th>
                        <th>İÇ/AY</th>
                        <th>DIŞ/AY</th>
                      </tr>
                    </thead>
                    <tbody className="text-end">
                      <tr>
                        <th className="text-start">Ordino satış</th>
                        <td>{formatTon(metrics.orderSales.monthTon)}</td>
                        <td>{formatTon(metrics.orderSales.yearTon)}</td>
                        <td>
                          {formatTon(metrics.orderSales.domesticMonthTon)}
                        </td>
                        <td>{formatTon(metrics.orderSales.exportMonthTon)}</td>
                      </tr>
                      <tr>
                        <th className="text-start">Nihai Sevkiyat</th>
                        <td>{formatTon(metrics.finalShipment.monthTon)}</td>
                        <td>{formatTon(metrics.finalShipment.yearTon)}</td>
                        <td>
                          {formatTon(metrics.finalShipment.domesticMonthTon)}
                        </td>
                        <td>
                          {formatTon(metrics.finalShipment.exportMonthTon)}
                        </td>
                      </tr>
                      <tr aria-hidden="true">
                        <td
                          colSpan={5}
                          className="border-0 bg-white"
                          style={{ height: 22, padding: 0 }}
                        ></td>
                      </tr>
                      <tr className="text-center">
                        <td></td>
                        <td></td>
                        <td></td>
                        <th className="table-dark">BP/AY</th>
                        <th className="table-dark">ÇSM/AY</th>
                      </tr>
                      <tr>
                        <th className="text-start">Nihai üretim</th>
                        <td>{formatTon(metrics.finalProduction.monthTon)}</td>
                        <td>{formatTon(metrics.finalProduction.yearTon)}</td>
                        <td>{formatTon(metrics.finalProduction.bpMonthTon)}</td>
                        <td>
                          {formatTon(metrics.finalProduction.csmMonthTon)}
                        </td>
                      </tr>
                    </tbody>
                  </Table>
                </div>

                {summary.warnings?.length > 0 && (
                  <Alert variant="warning" className="mt-3 mb-0">
                    {summary.warnings.map((warning) => (
                      <div key={warning}>{warning}</div>
                    ))}
                  </Alert>
                )}
              </Card.Body>
            </Card>

            <Card className="border-0 shadow-sm">
              <Card.Header className="bg-white border-0 px-4 pt-4 pb-2">
                <h5 className="fw-bold mb-1">E-posta önizlemesi</h5>
                <small className="text-muted">
                  Konu: {report.email.subject}
                </small>
              </Card.Header>
              <Card.Body className="p-4">
                <iframe
                  title="Üretim ve satış e-posta önizlemesi"
                  className="border rounded-3 overflow-auto mb-4"
                  srcDoc={report.email.html}
                  style={{
                    width: "100%",
                    minHeight: 760,
                    background: "#ffffff",
                  }}
                />

                <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3">
                  <Form.Group style={{ minWidth: 320 }}>
                    <Form.Label className="fw-semibold">Alıcı</Form.Label>
                    <Form.Select
                      value={recipientEmail}
                      onChange={(event) => {
                        setRecipientEmail(event.target.value);
                        setError("");
                        setSuccess("");
                      }}
                      disabled={isSending}
                    >
                      <option value="">Alıcı seçin</option>
                      {(report.recipientEmails || []).map((email) => (
                        <option key={email} value={email}>
                          {email}
                        </option>
                      ))}
                    </Form.Select>
                    <small className="text-muted">
                      Yüklediğiniz Excel dosyası e-postaya eklenecektir.
                    </small>
                  </Form.Group>
                  <Button
                    variant="success"
                    size="lg"
                    className="d-flex align-items-center justify-content-center gap-2"
                    onClick={handleSend}
                    disabled={isSending || isAnalyzing || !recipientEmail}
                  >
                    {isSending ? (
                      <Spinner animation="border" size="sm" />
                    ) : (
                      <FiMail size={20} />
                    )}
                    {isSending ? "Gönderiliyor..." : "Seçilen Alıcıya Gönder"}
                  </Button>
                </div>
              </Card.Body>
            </Card>
          </>
        )}
      </div>
    </Layout>
  );
}

export default ProductionSalesSummaryPage;
